import { useInternational } from '@/common/locale';
import { NoticeWindowInfo, useNoticeStore } from '@/config/stores/notice';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { webStorageController } from '@/common/utils/browser';
import router from '@/common/router';
import { isNull } from 'lodash-es';
import { ONE_SECOND, ONE_MINUTE } from '@/common/utils';
import { usePropertyStore } from '@/common/stores/property';
import { NoticeOverView } from '@/api-client/notice/model';
import { PATH } from '@/common/router/navigationGuard';

export const setup = () => {
  const { t } = useInternational();

  const { propertyMapObj } = storeToRefs(usePropertyStore());

  const noticeStore = useNoticeStore();
  const { checkPopupInfo, setClicked, startPopupInfoInterval, stopPopupInfoInterval } = noticeStore;
  const { noticePopupStatus, popupInfo, expandNoticeId, popupInterval, homeNoticeList, isLogin } =
    storeToRefs(noticeStore);

  const isShowPopupCheck = ref(false);

  const isHomeMode = computed(() => noticePopupStatus.value.operationType === 'home');

  // 창에서 표시할 리스트를 단일 진입점으로 통일
  const windowList = computed(() => {
    if (!isHomeMode.value) {
      return popupInfo.value;
    }

    return homeNoticeList.value;
  });

  const expandItemIndex = computed(() => {
    if (!expandNoticeId.value) return 0;

    const index = windowList.value.findIndex(
      (item: NoticeOverView) => item.id === expandNoticeId.value,
    );

    return index >= 0 ? index : 0;
  });

  const onCloseWindow = () => {
    noticePopupStatus.value = {
      isShow: false,
      operationType: noticePopupStatus.value.operationType,
    };
    expandNoticeId.value = 0;

    // 홈 모드에서는 임시 숨김/영속 숨김 저장을 하지 않음
    if (isHomeMode.value) return;

    const data = {
      popupInfo: popupInfo.value.map((item: NoticeWindowInfo) => ({
        id: item.id,
        title: item.title,
      })),
    };

    sessionStorage.setItem('noticePopupTempHiddenInfo', JSON.stringify(data));

    // '다시 보지 않기' 체크 상태면 로컬 스토리지에 영속 저장 (팝업 모드일 때만)
    if (isShowPopupCheck.value) {
      const stored = {
        isShowPopupCheck: true,
        popupInfo: data.popupInfo,
      };

      webStorageController.setItem({
        type: 'local',
        key: 'noticePopupInfo',
        value: JSON.stringify(stored),
      });
    }
  };

  const onExpandItem = async (isExpand: boolean, noticeId?: number) => {
    if (!isExpand || isNull(noticeId)) return;
    await setClicked(noticeId);
  };

  watch(
    () => noticePopupStatus.value.isShow,
    async (isShow) => {
      if (isShow) {
        const expandItem = windowList.value[expandItemIndex.value];

        if (expandItem && !expandItem.clicked) {
          await setClicked(expandItem.id);
        }

        isShowPopupCheck.value = false;
      } else {
        expandNoticeId.value = 0;
      }
    },
  );

  watch(router.currentRoute, async () => {
    const currentRoute = router.currentRoute.value;
    if (currentRoute.name === 'BrowserPopup' || currentRoute.path === PATH.PUBLIC_POPUP) {
      return;
    }

    // 임시 숨김 제거 후 공지 재표시 (DECISIONS.md)
    await sessionStorage.removeItem('noticePopupTempHiddenInfo');
    await startPopupInfoInterval(); // 이미 폴링 중이면 no-op
    await checkPopupInfo();
  });

  watch(isLogin, (loggedIn) => {
    if (!loggedIn) {
      stopPopupInfoInterval();
    }
  });

  onUnmounted(() => {
    stopPopupInfoInterval();
  });

  watch(
    () => propertyMapObj.value?.noticeCheckIntervalSec,
    async (value) => {
      const sec = Number(value ?? NaN);
      popupInterval.value = Number.isFinite(sec) && sec > 0 ? sec * ONE_SECOND : ONE_MINUTE;
    },
  );

  onMounted(() => {
    try {
      const saved = webStorageController.getItem({ type: 'local', key: 'noticePopupInfo' });

      if (!saved) return;

      const parsed = JSON.parse(saved);

      if (typeof parsed?.isShowPopupCheck === 'boolean') {
        isShowPopupCheck.value = parsed.isShowPopupCheck;
      }
    } catch {
      console.log('Error parsing noticePopupInfo from local storage');
    }
  });

  return {
    t,

    isShowPopupCheck,
    noticePopupStatus,
    windowList,
    expandItemIndex,

    onExpandItem,
    onCloseWindow,
  };
};
